// src/components/CommercialAgents.jsx
//
// Admin page to create, edit and (de)activate ambassadors
// (commercial_agent rows) - before this they could only be added in SQL.
// Backed by POST/PUT /agents in backend/routes/commercial/agents.ts, which
// are admin-only. An ambassador also needs the ROLE_SALES realm role in
// Keycloak, which this page can't grant - it says so after a creation.
//
// Same auth+fetch boilerplate as CommercialLeads.jsx.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Link, CircularProgress,
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { commercialGet, commercialPost, commercialPut } from '../utils/commercialApi';
import { INK } from '../utils/commercialColors';
import OnboardingResult from './commercial/OnboardingResult';

const EMPTY = { first_name: '', last_name: '', email: '', zone: '', commission_rate: '' };

export default function CommercialAgents() {
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const loginRequestedRef = useRef(false);

  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);

  /* ─── Auth ─── */
  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      // POST/PUT /agents are admin-only server side; this is defense-in-depth.
      if (!kc.hasRealmRole('ROLE_ADMIN')) {
        setForbidden(true);
        setAuthLoading(false);
        return;
      }
      setKcInstance(kc);
      setToken(kc.token ?? '');
      setAuthLoading(false);
    })().catch(console.error);
  }, []);

  /* ─── Data ─── */
  const loadAgents = useCallback(() => {
    if (!token) return;
    setLoading(true);
    commercialGet('/agents', token)
      .then((data) => setAgents(data.agents || []))
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const createAgent = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setCreated(null);
    try {
      const data = await commercialPost('/agents', token, form);
      setCreated({ ...data.agent, onboarding: data.onboarding });
      setForm(EMPTY);
      loadAgents();
    } catch (err) {
      setError(errorLabel(err.message));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({
      first_name: a.first_name, last_name: a.last_name, email: a.email || '',
      zone: a.zone || '', commission_rate: a.commission_rate ?? '',
    });
  };

  const saveAgent = async (a, overrides = {}) => {
    setError('');
    const body = editingId === a.id
      ? { ...editForm, active: Boolean(a.active), ...overrides }
      : {
        first_name: a.first_name, last_name: a.last_name, email: a.email || '',
        zone: a.zone || '', commission_rate: a.commission_rate ?? 0, active: Boolean(a.active), ...overrides,
      };
    try {
      await commercialPut(`/agents/${a.id}`, token, body);
      setEditingId(null);
      loadAgents();
    } catch (err) {
      setError(errorLabel(err.message));
    }
  };

  if (authLoading) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Chargement…</Typography>;
  }
  if (forbidden) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Accès refusé.</Typography>;
  }

  const field = (state, setState, name, label, props = {}) => (
    <TextField
      size="small"
      label={label}
      value={state[name]}
      onChange={(e) => setState((f) => ({ ...f, [name]: e.target.value }))}
      {...props}
    />
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Link component={RouterLink} to="/admin/commercial" sx={{ display: 'block', mb: 1 }}>
            ← Retour au dashboard
          </Link>
          <Typography variant="h4">Ambassadeurs</Typography>
        </Box>
        <Button variant="outlined" onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}>
          Logout
        </Button>
      </Box>
      <Typography sx={{ color: INK.muted, mb: 3, maxWidth: 680 }}>
        À la création, l’ambassadeur reçoit son accès (compte QRProofs créé si besoin, rôle ambassadeur)
        et un e-mail de bienvenue qui explique comment prospecter, installer une borne, son parrainage et
        où trouver l’aide. S’il a déjà un compte QRProofs, saisissez exactement son e-mail.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {created && (
        <>
          <Alert severity="info" sx={{ mb: 1 }}>
            {created.first_name} {created.last_name} est créé, code de parrainage <b>{created.referral_code}</b>.
          </Alert>
          <OnboardingResult result={created.onboarding} email={created.email} />
        </>
      )}

      <Paper elevation={0} component="form" onSubmit={createAgent}
        sx={{ p: 2, mb: 3, borderRadius: 3, border: `1px solid ${INK.gridline}` }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Nouvel ambassadeur</Typography>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          {field(form, setForm, 'first_name', 'Prénom', { required: true })}
          {field(form, setForm, 'last_name', 'Nom', { required: true })}
          {field(form, setForm, 'email', 'E-mail du compte', { required: true, type: 'email', sx: { minWidth: 260 } })}
          {field(form, setForm, 'zone', 'Zone (ex. Beaune)')}
          {field(form, setForm, 'commission_rate', 'Commission %', { type: 'number', inputProps: { min: 0, max: 100, step: 0.5 }, sx: { width: 130 } })}
          <Button type="submit" variant="contained" disabled={creating}>
            {creating ? 'Création…' : 'Créer'}
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${INK.gridline}`, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Ambassadeur</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Zone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Commission</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Leads</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {!loading && agents && agents.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: INK.muted }}>Aucun ambassadeur.</TableCell>
              </TableRow>
            )}
            {!loading && agents && agents.map((a) => (editingId === a.id ? (
              <TableRow key={a.id}>
                <TableCell>
                  <Stack spacing={1}>
                    {field(editForm, setEditForm, 'first_name', 'Prénom')}
                    {field(editForm, setEditForm, 'last_name', 'Nom')}
                  </Stack>
                </TableCell>
                <TableCell>{field(editForm, setEditForm, 'email', 'E-mail', { type: 'email' })}</TableCell>
                <TableCell>{field(editForm, setEditForm, 'zone', 'Zone')}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{a.referral_code}</TableCell>
                <TableCell>{field(editForm, setEditForm, 'commission_rate', '%', { type: 'number', sx: { width: 90 } })}</TableCell>
                <TableCell>{a.leads}</TableCell>
                <TableCell />
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" variant="contained" onClick={() => saveAgent(a)}>Enregistrer</Button>
                  <Button size="small" onClick={() => setEditingId(null)}>Annuler</Button>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={a.id} sx={{ opacity: a.active ? 1 : 0.55 }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Link component={RouterLink} to={`/admin/commercial/agent/${a.id}`}>{a.first_name} {a.last_name}</Link>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{a.email || '—'}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{a.zone || '—'}</TableCell>
                <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>{a.referral_code}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{Number(a.commission_rate || 0)} %</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{a.leads}</TableCell>
                <TableCell>
                  <Chip size="small" label={a.active ? 'Actif' : 'Désactivé'} color={a.active ? 'success' : 'default'} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => startEdit(a)}>Modifier</Button>
                  <Button size="small" color={a.active ? 'error' : 'primary'} onClick={() => saveAgent(a, { active: !a.active })}>
                    {a.active ? 'Désactiver' : 'Réactiver'}
                  </Button>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}

function errorLabel(message) {
  if (/already uses this email/i.test(message)) return 'Un ambassadeur utilise déjà cet e-mail.';
  if (/valid email/i.test(message)) return 'E-mail invalide.';
  if (/commission_rate/i.test(message)) return 'La commission doit être entre 0 et 100 %.';
  if (/first_name|last_name/i.test(message)) return 'Prénom et nom sont obligatoires.';
  return message || 'Erreur';
}
